<template>
  <a-tabs activeKey="reset"
          class="tabs">
    <a-tab-pane key="reset"
                :tab="$t('LoginPage.ResetPassword.resetPassword')">
      <a-form ref="form"
              class="form"
              :model="reset"
              name="basic"
              :wrapper-col="{ span: 24 }"
              autocomplete="off"
              :rules="rules"
              layout="vertical"
              @finish="onFinish"
              @finish-failed="onFinishFailed">
        <a-form-item name="mobile">
          <a-input v-model:value="reset.mobile"
                   size="large"
                   autocomplete="off"
                   :placeholder="$t('LoginPage.ResetPassword.enterMobile')">
            <template #prefix>
              <mobile-outlined />
            </template>
          </a-input>
        </a-form-item>
        <a-form-item name="password">
          <a-input-password v-model:value="reset.password"
                            size="large"
                            autocomplete="off"
                            :placeholder="$t('LoginPage.ResetPassword.enterPassword')">
            <template #prefix>
              <lock-outlined />
            </template>
          </a-input-password>
        </a-form-item>
        <a-form-item name="password_repeat">
          <a-input-password v-model:value="reset.password_repeat"
                            size="large"
                            autocomplete="off"
                            :placeholder="$t('LoginPage.ResetPassword.repeatPassword')">
            <template #prefix>
              <lock-outlined />
            </template>
          </a-input-password>
        </a-form-item>
        <!-- <a-row :gutter="10">
        <a-col :span="16">
          <a-form-item name="vcode">
            <a-input v-model:value="reset.vcode"
                     size="large"
                     autocomplete="off"
                     :placeholder="$t('LoginPage.ResetPassword.enterVCode')">
              <template #prefix>
                <barcode-outlined />
              </template>
            </a-input>
          </a-form-item>
        </a-col>
        <a-col :span="8"
               class="vcode">
          <img :src='reset.vcodeGraph'
               :alt="$t('LoginPage.ResetPassword.clickToRefresh')"
               @click='refreshVCodeGraph' />
        </a-col>
      </a-row> -->
        <a-row :gutter="10">
          <a-col :span="16">
            <a-form-item name="captcha">
              <a-input v-model:value="reset.captcha"
                       size="large"
                       autocomplete="off"
                       :placeholder="$t('LoginPage.ResetPassword.enterCaptcha')">
                <template #prefix>
                  <code-outlined />
                </template>
              </a-input>
            </a-form-item>
          </a-col>
          <a-col :span="8"
                 class="captcha">
            <count-down-button html-type="submit"
                               :text="$t('LoginPage.ResetPassword.getCaptcha')"
                               :counting-text-post="$t('LoginPage.ResetPassword.retryAfter')"
                               :disabled="!(reset.isMobileValid)"
                               size="large"
                               @click="getCaptcha"></count-down-button>
          </a-col>
        </a-row>
        <a-row class="footer">
          <a-button type="primary"
                    html-type="submit"
                    size="large">{{ $t('LoginPage.ResetPassword.resetPasswordButton') }}</a-button>
          <a v-if="!disableToSignin"
             class="link"
             type="primary"
             size="large"
             @click="toSignin">{{ $t('LoginPage.ResetPassword.signinWithExistingAccount') }}</a>
        </a-row>
      </a-form>
    </a-tab-pane>
  </a-tabs>
</template>


<script setup lang="ts">
import { CountDownButton } from '@iota-cn/ui-components'
import { Validator } from '@iota-cn/util-validation'
import { message } from 'ant-design-vue'
import type { Rule } from 'ant-design-vue/lib/form'
import { container } from 'tsyringe'
import { reactive, ref } from 'vue'
import type { Router } from 'vue-router'
import { useLoginStore } from '../..'
import { CORE_AXIOS, CORE_ROUTER, Http } from '../../../../core'
import { SIGNIN_PATH } from '../constants'
import { useI18n } from 'vue-i18n'

// eslint-disable-next-line vue/require-prop-types
const props = defineProps(['disableToSignin', 'mobileValidator'])

const emit = defineEmits(['success'])

const userStore = useLoginStore()

const axios = container.resolve<Http>(CORE_AXIOS)
const router = container.resolve<Router>(CORE_ROUTER)

const { t } = useI18n()

const form = ref()
const toSignin = () => {
  router.push({ path: SIGNIN_PATH })
}

const reset = reactive({
  password: '',
  password_repeat: '',
  mobile: '',
  vcode: '',
  vcodeGraph: '',
  isVCodeValid: false,
  captcha: '',
  isMobileValid: false
})

const refreshVCodeGraph = async () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios.get(`/v1/api/validations/codes?graph=${Math.random()}`).then((res: any) => {
    reset.vcodeGraph = res
  })
}

refreshVCodeGraph()

const getCaptcha = async () => {
  const mobile = reset.mobile
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  axios.post(userStore.api?.fetchSmsValidationCode || '/v1/api/validations/smses', { mobile }, { raw: true }).then((res: any) => {
    if (res.status === 403) {
      message.error(t('LoginPage.ResetPassword.captchaRequestFrequent'))
    }
  })
}

const validateMobile = async (rule: Rule, value: string) => {
  if (Validator.isPhone(value)) {
    if (props.mobileValidator) {
      return props.mobileValidator(rule, value).then(() => {
        reset.isMobileValid = true
      }).catch((e: any) => {
        reset.isMobileValid = false
        return Promise.reject(e)
      })
    }
    else {
      return new Promise<void>((resolve, reject) => {
        axios.get(`/v1/api/validations/mobiles/validate?mobile=${value}`, undefined, { raw: true })
          .then((res: any) => {
            if (res.status === 204) {
              reset.isMobileValid = false
              reject(t('LoginPage.ResetPassword.enterRegisteredMobile'))
            } else if (res.status === 400) {
              reset.isMobileValid = true
              resolve(undefined)
            }
          }).catch((res: any) => {
            if (res.status === 400) {
              reset.isMobileValid = true
              resolve(undefined)
            }
          })
      })
    }
  } else {
    reset.isMobileValid = false
    return Promise.reject(t('LoginPage.ResetPassword.enterValidMobile'))
  }
}

const validateName = async (_rule: Rule, value: string) => {
  if (Validator.isUserName(value)) {
    return axios.get(`/v1/api/validations/usernames/validate?username=${value}`)
      .then(() => {
        return Promise.resolve()
      }).catch(() => {
        return Promise.reject(t('LoginPage.ResetPassword.enterUnusedUsername'))
      })
  } else {
    return Promise.reject(t('LoginPage.ResetPassword.enterValidMobile'))
  }
}

const validatePass = async (_rule: Rule, value: string) => {
  if (Validator.isPassword(value)) {
    if (value !== reset.password_repeat) {
      return Promise.reject(t('LoginPage.ResetPassword.passwordMismatch'))
    } else {
      form.value.clearValidate('password_repeat')
      return Promise.resolve()
    }
  } else {
    return Promise.reject(t('LoginPage.ResetPassword.enterValidPassword'))
  }
}

const validatePassRepeat = async (_rule: Rule, value: string) => {
  if (Validator.isPassword(value)) {
    if (value !== reset.password) {
      return Promise.reject(t('LoginPage.ResetPassword.passwordMismatch'))
    } else {
      form.value.clearValidate('password')
      return Promise.resolve()
    }
  } else {
    return Promise.reject(t('LoginPage.ResetPassword.enterValidPassword'))
  }
}

const validateVCode = async (_rule: Rule, value: string) => {
  if (value === undefined || value.length < 6) {
    reset.isVCodeValid = false
    return Promise.reject(t('LoginPage.ResetPassword.enterValidVCode'))
  }

  return axios.get(`/v1/api/validations/codes/validate?vcode=${value}`)
    .then(() => {
      reset.isVCodeValid = true
      return Promise.resolve()
    }).catch(() => {
      reset.isVCodeValid = false
      return Promise.reject(t('LoginPage.ResetPassword.enterValidVCode'))
    })
}

const validateCaptcha = async (_rule: Rule, value: string) => {
  if (value === undefined || value.length < 6) {
    return Promise.reject(t('LoginPage.ResetPassword.enterVCode'))
  }
  return axios.get(`${userStore.api?.validateSmsCode || '/v1/api/validations/smses/validate'}?mobile=${reset.mobile}&captcha=${value}`)
    .then(() => {
      return Promise.resolve()
    }).catch(() => {
      return Promise.reject(t('LoginPage.ResetPassword.enterValidVCode'))
    })
}

const rules: Record<string, Rule[]> = {
  username: [{ required: true, validator: validateName, trigger: 'change' }],
  mobile: [{ required: true, validator: validateMobile, trigger: 'change' }],
  vcode: [{ required: true, validator: validateVCode, trigger: 'change' }],
  captcha: [{ required: true, validator: validateCaptcha, trigger: 'change' }],
  password: [{ required: true, validator: validatePass, trigger: 'change' }],
  password_repeat: [{ required: true, validator: validatePassRepeat, trigger: 'change' }],
}

const onFinish = () => {
  const user = {
    password: reset.password,
    captcha: reset.captcha,
    vcode: reset.vcode,
    mobile: reset.mobile
  }

  axios.post('/v1/api/accounts/password/reset_by_mobile', user).then(() => {
    if (!props.disableToSignin) {
      message.success(t('LoginPage.ResetPassword.resetPasswordSuccess'))
      router.push({ path: SIGNIN_PATH })
    }
    emit('success')
  }).catch(() => {
    message.error(t('LoginPage.ResetPassword.resetPasswordFailure'))
  })
}

const onFinishFailed = () => {
  // message.error(t('LoginPage.ResetPassword.resetPasswordFailure'))
}
</script>


<style scoped lang="less">
.tabs {
  width: 100%;

  .form {
    width: 100%;

    .captcha,
    .vcode {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 24px;

      img {
        height: 40px;
        cursor: pointer;
        width: 100%;
      }

      button {
        width: 100%;
      }
    }

    .footer {
      display: flex;
      justify-content: space-between;
      align-items: center;

      button {
        flex: 1
      }

      .link {
        width: 50%;
        margin: 10px 0;
        text-align: right;
      }
    }
  }
}
</style>
